import { Button, Collapse, Menu, MenuItem } from '@material-ui/core';
import React, { useRef } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
    NomineeStatusErrorRef,
    NomineeStatusError,
} from '../../components/common/NomineeStatusError';
import { apDividendFilters } from '../../components/dashboard/commercial/AdditionalPurchase/types';
import { RedeemPaths } from '../../components/dashboard/commercial/Redemption/routes';
import { CommercialRootPath } from '../../components/dashboard/commercial/routeConfig';
import { STPPaths } from '../../components/dashboard/commercial/STP/types';
import { SwitchPaths } from '../../components/dashboard/commercial/Switch/types';
import {
    redemptionFilters,
    stpFilters,
    switchFilters,
    swpFilters,
} from '../../components/dashboard/commercial/Switch/utils';
import { SWPPaths } from '../../components/dashboard/commercial/SWP/routes';
import { dateConvert } from '../../components/dashboard/portfolios/utils';
import en from '../../lang/en-us';
import { RootStateType } from '../../redux-store/reducers';
import { IsAllow } from '../../redux-store/types/portfolio';
import { formatAsIndianCurrency } from '../../utils/currency';
const { dashboard, common } = en;

interface ListCardPortfolioTypes {
    fund?: string;
    investedValue?: string | number | null;
    marketValue?: string | number | null;
    gainLoss?: string | number | null;
    gainLossPercent?: string | number | null;
    holding?: number | null;
}

interface ListCardFund extends ListCardPortfolioTypes {
    balanceUnits?: string | number | null;
    isDemat?: boolean;
    xirr?: number | null;
    scheme: string;
    assetType?: string;
    schemeType?: string;
    navDate?: Date;
    nav?: number;
    amcDetails?: any;
    isin?: string;
}

interface ListCardChildProps extends ListCardPortfolioTypes {
    icon: string;
    href?: string;
    navigationAction?: Function;
    isLastCard?: boolean;
    hasNextScheme?: boolean;
    onNextScheme?: Function;
}
export default function ListCards({
    fund = '',
    icon = '',
    holding = 0,
    investedValue = 0,
    marketValue = 0,
    gainLoss = 0,
    gainLossPercent = 0,
    href,
    navigationAction = () => {},
    isLastCard = false,
    hasNextScheme = false,
    onNextScheme = () => {},
}: ListCardChildProps) {
    return (
        <a
            onClick={() => navigationAction()}
            className="list-cards-single-link"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigationAction();
                } else if (e.key === 'Tab' && isLastCard && !e.shiftKey && hasNextScheme) {
                    e.preventDefault();
                    onNextScheme();
                }
            }}
            role="button"
            aria-label={`View details for ${fund}`}
        >
            <div className="list-cards-single">
                <div className="list-cards-top">
                    <div className="list-cards-image">
                        <img
                            src={`/images/client-logos/${icon}.png`}
                            alt=""
                            className="list-logo"
                        />
                    </div>
                    <h3>{fund}</h3>
                    <p>
                        {dashboard.portfolioDetails.list.holdingPercentage} :{' '}
                        <span>{holding}%</span>
                    </p>
                </div>
                <div className="list-cards-bottom">
                    <div className="list-cards-bottom-info">
                        <h5>
                            <img src="/images/invested-value-icon.svg" alt="" />
                            {dashboard.dbPortfolio.investedvalue}
                        </h5>
                        <p>{investedValue}</p>
                    </div>
                    <div className="list-cards-bottom-info">
                        <h5>
                            <img src="/images/market-value-icon.svg" alt="" />
                            {dashboard.dbPortfolio.marketvalue}
                        </h5>
                        <p>{marketValue}</p>
                    </div>
                    <div className="list-cards-bottom-info">
                        <h5>
                            <img src="/images/gain-loss-icon.svg" alt="" />
                            {dashboard.dbPortfolio.gainLoss}
                        </h5>
                        <p>
                            {gainLoss}{' '}
                            <span className={Number(gainLossPercent) >= 0 ? 'profit' : 'loss'}>
                                <img
                                    src={
                                        Number(gainLossPercent) >= 0
                                            ? '/images/profit-arrow.svg'
                                            : '/images/loss-arrow.svg'
                                    }
                                />
                                {gainLossPercent}%
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </a>
    );
}

export function ListCardsFund({
    isin = '',
    scheme = '',
    xirr = 0,
    amcDetails = {},
    investedValue = 0,
    marketValue = 0,
    gainLoss = 0,
    gainLossPercent = 0,
    balanceUnits = 0,
    isDemat = false,
    assetType = '',
    schemeType = '',
    navDate = new Date(),
    nav = 0,
}: ListCardFund) {
    const [viewMore, setViewMore] = useState<boolean>(false);
    const history = useHistory();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const nomineeErrorRef = useRef<NomineeStatusErrorRef>(null);
    const handleClose = () => {
        setAnchorEl(null);
    };

    const schemeFilterAllow = apDividendFilters.every((filter) => filter.call({}, amcDetails));
    const redemptionAllow = redemptionFilters.every((filter) => filter.call({}, amcDetails));
    const switchAllow = switchFilters.every((filter) => filter.call({}, amcDetails));
    const stpAllow = stpFilters.every((filter) => filter.call({}, amcDetails));
    const swpAllow = swpFilters.every((filter) => filter.call({}, amcDetails));

    const { ftTransactionFlag } = useSelector(({ authReducer }: RootStateType) => ({
        ftTransactionFlag: authReducer?.ftTransactions,
    }));

    return (
        <div className="list-cards-single">
            <div className="list-cards-top">
                <h3>{scheme}</h3>
                {/* <NomineeStatusError ref={nomineeErrorRef} /> */}
                {ftTransactionFlag === IsAllow.YES && !isDemat && (
                    <div className="list-cards-top-right">
                        <a
                            className={`invest-more ${
                                !isin || !schemeFilterAllow ? 'disabled-button' : ''
                            }`}
                            onClick={() =>
                                isin &&
                                schemeFilterAllow &&
                                history.push(
                                    `${CommercialRootPath}/ap-investment/${amcDetails.isin}?amc=${amcDetails.amc}`,
                                    {
                                        additionalPurchaseFolio: amcDetails,
                                        sipFlag: false,
                                    },
                                )
                            }
                        >
                            Invest More
                        </a>
                        <Button
                            id="invest-dots"
                            aria-controls={open ? 'invest-dots-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                        >
                            <p>
                                <span></span>
                                <span></span>
                                <span></span>
                            </p>
                        </Button>
                        <Menu
                            id="invest-dots-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    // if (
                                    //     ![IsAllow.YES, 'O'].includes(
                                    //         amcDetails?.nomineeStatus?.toUpperCase(),
                                    //     )
                                    // ) {
                                    //     nomineeErrorRef.current?.showNomineeError();
                                    // } else {
                                    history.push(`${CommercialRootPath}${SwitchPaths.SwitchTo}`, {
                                        switchFromScheme: amcDetails,
                                    });
                                    // }
                                }}
                                disabled={
                                    amcDetails.swtAllow === IsAllow.No ||
                                    amcDetails.isin === 'null' ||
                                    !amcDetails.isin ||
                                    amcDetails.availableUnits === 0 ||
                                    !switchAllow
                                }
                            >
                                Switch
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    // if (
                                    //     ![IsAllow.YES, 'O'].includes(
                                    //         amcDetails?.nomineeStatus?.toUpperCase(),
                                    //     )
                                    // ) {
                                    // nomineeErrorRef.current?.showNomineeError();
                                    // } else {
                                    history.push(`${CommercialRootPath}${STPPaths.to}`, {
                                        stpFromScheme: amcDetails,
                                    });
                                    // }
                                }}
                                disabled={
                                    amcDetails.isin === 'null' ||
                                    !amcDetails.isin ||
                                    amcDetails.availableUnits === 0 ||
                                    !stpAllow
                                }
                            >
                                STP
                            </MenuItem>
                            <MenuItem
                                disabled={
                                    amcDetails.redAllow === IsAllow.No ||
                                    amcDetails.isin === 'null' ||
                                    !amcDetails.isin ||
                                    amcDetails.availableUnits === 0 ||
                                    !redemptionAllow
                                }
                                onClick={() => {
                                    // if (
                                    //     ![IsAllow.YES, 'O'].includes(
                                    //         amcDetails?.nomineeStatus?.toUpperCase(),
                                    //     )
                                    // ) {
                                    // nomineeErrorRef.current?.showNomineeError();
                                    // } else {
                                    history.push(
                                        `${CommercialRootPath}${RedeemPaths.RedeemMain}${amcDetails.isin}?amc=${amcDetails.amc}`,
                                        {
                                            additionalPurchaseFolio: amcDetails,
                                        },
                                    );
                                    // }
                                }}
                            >
                                Redemption
                            </MenuItem>
                            <MenuItem
                                disabled={
                                    amcDetails.isin === 'null' ||
                                    !amcDetails.isin ||
                                    amcDetails.availableUnits === 0 ||
                                    !swpAllow
                                }
                                onClick={() => {
                                    // if (
                                    //     ![IsAllow.YES, 'O'].includes(
                                    //         amcDetails?.nomineeStatus?.toUpperCase(),
                                    //     )
                                    // ) {
                                    //     nomineeErrorRef.current?.showNomineeError();
                                    // } else {
                                    history.push(
                                        `${CommercialRootPath}${SWPPaths.SWPMain}${amcDetails.isin}?amc=${amcDetails.amc}`,
                                        {
                                            swpFolio: amcDetails,
                                        },
                                    );
                                    // }
                                }}
                            >
                                SWP
                            </MenuItem>
                            {/* <MenuItem onClick={handleClose}>STP</MenuItem>
                            <MenuItem onClick={handleClose}>SWP</MenuItem> */}
                        </Menu>
                    </div>
                )}
            </div>
            <div className="list-cards-tags">
                {!!assetType && <span>{assetType}</span>}
                {!!schemeType && <span>{schemeType}</span>}
            </div>
            <div className="list-cards-bottom">
                <div className="list-cards-bottom-info">
                    <h5>{dashboard.dbPortfolio.investedvalue}</h5>
                    <p>{formatAsIndianCurrency(Number(investedValue))}</p>
                </div>
                <div className="list-cards-bottom-info">
                    <h5>{dashboard.dbPortfolio.marketvalue}</h5>
                    <p>{formatAsIndianCurrency(Number(marketValue))}</p>
                </div>
                <div className="list-cards-bottom-info">
                    <h5>{dashboard.dbPortfolio.gainLoss}</h5>
                    <p>
                        {formatAsIndianCurrency(Number(gainLoss))}{' '}
                        <span
                            style={{
                                color: Number(gainLossPercent) >= 0 ? '#1bc387' : '#eb5757',
                            }}
                            className="profit"
                        >
                            <img
                                src={
                                    Number(gainLossPercent) >= 0
                                        ? '/images/profit-arrow.svg'
                                        : '/images/loss-arrow.svg'
                                }
                            />
                            {gainLossPercent}%
                        </span>
                    </p>
                </div>
            </div>
            <div className="list-cards-more-content">
                <div className="list-cards-more">
                    <p>
                        ( {common.asOn} {dateConvert(navDate)})
                    </p>
                </div>
                <div className="list-cards-show-nav-balance-units">
                    <div className="list-cards-bottom-info">
                        <h5>{dashboard.portfolioSchemes.nav}</h5>
                        <p>{formatAsIndianCurrency(nav)}</p>
                    </div>
                    <div className="list-cards-bottom-info">
                        <h5>{dashboard.portfolioSchemes.balanceUnits}</h5>
                        <p>{balanceUnits}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
